
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import CREATE_USER_EXPERIENCE from "@/apollo/profile/experiences/CreateExperience.gql";
import UPDATE_USER_EXPERIENCE from "@/apollo/profile/experiences/UpdateExperience.gql";
import REMOVE_EXPERIENCE from "@/apollo/profile/experiences/RemoveExperience.gql";

import ModalFullScreen from "@/components/modal/ModalFullScreen.vue";
import ModalHeader from "@/components/modal/ModalHeader.vue";
import FormDatepicker from "@/components/form/FormDatepickerNew.vue";
import FormGroup from "@/components/form/FormGroupNew.vue";
import FormTextarea from "@/components/form/FormTextarea.vue";
import SkillsMultiselect from "@/components/autocompleteMultiselects/SkillsMultiselectNew.vue";
import IndustriesMultiselect from "@/components/autocompleteMultiselects/IndustriesMultiselectNew.vue";
import ExperienceEmploymentTypeSelect from "@/components/autocompleteMultiselects/ExperienceEmploymentTypeSelect.vue";
import UIButton from "@/components/button/ui-button.vue";

import {
    Experience_Skill,
    Industry,
    User_Experience,
} from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";

@Component({
    components: {
        ModalFullScreen,
        ModalHeader,
        FormDatepicker,
        FormGroup,
        FormTextarea,
        SkillsMultiselect,
        IndustriesMultiselect,
        ExperienceEmploymentTypeSelect,
        UIButton,
        LoadingOverlay
    },
})
export default class ExperienceForm extends Vue {
    @Prop({ type: Object, default: undefined }) readonly experience?: User_Experience;
    @Prop({ type: String, required: true }) readonly userId!: string;
    @Prop({ type: Boolean, default: false }) readonly showDialog!: boolean;
    @Prop({ type: String, required: true}) readonly currentUserId!: string;

    id?: string = undefined;
    companyName: string = "";
    jobTitle: string = "";
    skillsIds: number[] = [];
    industry: Industry[] = [];
    summary: string = "";
    startDate: string | null = null;
    endDate: string | null = null;
    location: string = "";
    employmentType: string = "Full Time";
    ongoing: boolean = false;

    @Watch("experience", { immediate: true })
    watchProfile(experience: User_Experience): void {
        this.id = experience?.id;
        this.companyName = experience?.company_name ?? "";
        this.jobTitle = experience?.job_title ?? "";
        this.skillsIds = experience?.skills.map(({ skill }) => skill.id) ?? [];
        this.industry = experience?.industry ? [experience.industry] : [];
        this.summary = experience?.summary ?? "";
        this.startDate = experience?.start_date ?? null;
        this.endDate = experience?.end_date ?? null;
        this.location = experience?.location ?? "";
        this.employmentType = experience?.employment_type ?? "Full Time";
        this.ongoing = Boolean(experience?.ongoing);
    }

    get modalTitle(): string {
        const prefix = this.isEditing ? "Edit" : "Add";
        return `${prefix} Work Experience`;
    }

    get isEditing(): boolean {
        return Boolean(this.experience);
    }

    get experienceIndustryId(): number | undefined {
        return this.industry[0]?.id;
    }

    get experienceSkills(): Partial<Experience_Skill>[] {
        return this.skillsIds.map(skillId => ({
            experience_id: this.id,
            skill_id: skillId,
        }));
    }

 async removeExperience(): Promise<void> {
        try {
            await this.remove();
            this.$emit("removed");

            this.$eventBus.emit(
                NOTIFICATION,
                Message.successMessage(`${this.experience.company_name !== "" ? this.experience.company_name : "Selected"}  is removed from your experience.`)
            );
        } catch (error) {
            var msg = "";
            if (error instanceof Error) {
                msg = error.message;
            } else {
                msg = error as string;
            }
            this.$eventBus.emit(
                NOTIFICATION,
                Message.errorMessage(msg)
            );
        }
    }

    async remove(): Promise<void> {
        await this.$apollo.mutate({
            mutation: REMOVE_EXPERIENCE,
            variables: {
                id: this.experience.id,
            },
        });
    }

    get experienceSet(): Partial<User_Experience> {
        return {
            user_id: this.userId,
            job_title: this.jobTitle,
            company_name: this.companyName,
            employment_type: this.employmentType,
            ongoing: this.ongoing,
            location: this.location,
            summary: this.summary,
            start_date: this.startDate,
            end_date: this.ongoing ? null : this.endDate,
            industry_id: this.experienceIndustryId,
            added_by_id: this.currentUserId
        };
    }

    closeModal(): void {
        this.$emit("toggle", false);
    }

    async submit(): Promise<void> {
        try {
            if (this.isEditing) {
                await this.update();
            } else {
                await this.create();
            }

            const successMessage = this.isEditing
                ? `${this.companyName} was successfully updated.`
                : `${this.companyName} added to your experience.`;
            this.$eventBus.emit(
                NOTIFICATION,
                Message.successMessage(successMessage)
            );
        } catch (error) {
            var msg = "";
            if (error instanceof Error) {
                msg = error.message;
            } else {
                msg = error as string;
            }
            this.$eventBus.emit(
                NOTIFICATION,
                Message.errorMessage(msg)
            );
        } finally {
            this.closeModal();
        }
    }

    async create(): Promise<void> {
        await this.$apollo.mutate({
            mutation: CREATE_USER_EXPERIENCE,
            variables: {
                experienceSet: {
                    ...this.experienceSet,
                    skills: {
                        data: this.experienceSkills,
                    },
                },
            },
        });
        this.$emit("added");
    }

    async update(): Promise<void> {
        await this.$apollo.mutate({
            mutation: UPDATE_USER_EXPERIENCE,
            variables: {
                experienceSet: this.experienceSet,
                skillsSet: this.experienceSkills,
                id: this.id,
            },
        });
        this.$emit("updated");
    }
}
